import * as Sentry from "@sentry/react";
import { CONSTANTS } from "./config/constants";
import { initAnalytics } from "./lib/analytics";

// Local tracing: VITE_SENTRY_SPOTLIGHT=true streams every trace to Spotlight
// (`npx @spotlightjs/spotlight`) and, with the backend's SENTRY_SPOTLIGHT, joins
// each request to its backend spans. No DSN, so nothing reaches sentry.io.
const isSpotlight =
  import.meta.env.DEV && import.meta.env.VITE_SENTRY_SPOTLIGHT === "true";
const enabled = CONSTANTS.IS_PRODUCTION || CONSTANTS.IS_STAGING || isSpotlight;
const env = (CONSTANTS.APP_ENV as string).toLowerCase();

// Only sample performance traces in production. QA (staging) and local capture
// errors only, so non-prod traffic doesn't burn the Sentry quota.
const tracesSampleRate = CONSTANTS.IS_PRODUCTION ? 0.2 : isSpotlight ? 1 : 0;
// Profiles ride on sampled traces (page loads, navigations) in a quarter of
// production sessions. The browser only starts the profiler when the document
// is served with `Document-Policy: js-profiling` (vercel.json).
const profileSessionSampleRate = CONSTANTS.IS_PRODUCTION ? 0.25 : 0;

if (!Sentry.getClient()) {
  Sentry.init({
    dsn: isSpotlight
      ? undefined
      : "https://cd1a3b3448cd469f1f4d6e98485df20f@o4508974719893504.ingest.de.sentry.io/4510905278070864",
    sendDefaultPii: true,
    environment: env,
    enabled,
    integrations: [
      ...(isSpotlight ? [Sentry.spotlightBrowserIntegration()] : []),
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    // PostHog owns session replay now (see lib/analytics). Sentry keeps only
    // the error-triggered replay, which PostHog has no equivalent for.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    enableLogs: true,
    tracesSampleRate, // prod only (20%); 0 in QA/local
    profileSessionSampleRate,
    profileLifecycle: "trace",
    // Whichever backend this bundle was built for (brand and environment come
    // from env). Sentry matches strings as substrings of the request URL, so
    // every versioned path under it carries the trace headers.
    tracePropagationTargets: CONSTANTS.BACKEND_URL ? [CONSTANTS.BACKEND_URL] : [],
  });
}

initAnalytics();
